import React from 'react';

import Tippy from '@tippyjs/react';
import { PopoverButton as HeadlessPopoverButton } from '@headlessui/react';

import { cn } from 'lib/helpers/utility';

import Icon from 'components/shared/Icon/Icon';
import Loader from 'components/shared/Loader/Loader';

import CustomPopoverTrigger from './components/CustomPopoverTrigger';
import {
  getButtonClasses,
  getDropdownIconColor,
  getIconColor,
  getIconRightColor,
  getSeparatorClasses,
  getTextClasses
} from './lib/getClasses';

const PopoverButton = ({
  closePopover,
  buttonRef,
  onButtonClick,
  dataQa,
  open,
  buttonTheme,
  text,
  isSeparator,
  isBorder,
  buttonClassName,
  activeClassNames,
  disabled,
  buttonThemeType,
  loading,
  icon,
  iconSize,
  iconColor,
  iconsShade,
  iconStroke,
  iconClassName,
  textClassName,
  iconRight,
  iconRightSize,
  iconRightColor,
  iconRightClassName,
  isDropdown,
  tooltip,
  isTooltipDark = true,
  tooltipPlacement = 'bottom',
  customButton,
  customButtonTrigger,
  customButtonWrapperClassName,
  customButtonClassName,
  setIsOpen,
  removeWidthAndHeight = false,
  onOpen
}) => {
  // If custom buttons are provided, render them instead
  if (customButton || customButtonTrigger) {
    return (
      <CustomPopoverTrigger
        {...{
          closePopover,
          buttonRef,
          onButtonClick,
          dataQa,
          open,
          disabled,
          tooltip,
          isTooltipDark,
          tooltipPlacement,
          customButton,
          customButtonTrigger,
          customButtonWrapperClassName,
          customButtonClassName,
          setIsOpen,
          onOpen
        }}
      />
    );
  }

  return (
    <Tippy
      disabled={!tooltip}
      content={tooltip}
      placement={tooltipPlacement}
      className={isTooltipDark ? 'tippy-dark' : ''}>
      <HeadlessPopoverButton
        ref={buttonRef}
        onClick={(e) => {
          if (!open) onOpen?.();
          onButtonClick?.(e);
        }}
        data-qa={dataQa}
        className={getButtonClasses({
          open,
          buttonTheme,
          text,
          isSeparator,
          isBorder: isBorder ?? !text,
          buttonClassName,
          activeClassNames,
          disabled,
          buttonThemeType,
          removeWidthAndHeight
        })}
        disabled={disabled}>
        {/* Loading or Icon */}
        {loading ? (
          <Loader color={buttonTheme} shade={open ? 200 : 50} className="mr-1" />
        ) : typeof icon === 'object' ? (
          <div
            className={cn('mr-[6px]', disabled && '!cursor-not-allowed', iconClassName)}
            disabled={disabled}>
            {icon}
          </div>
        ) : (
          icon && (
            <Icon
              icon={icon}
              size={iconSize}
              color={getIconColor({
                disabled,
                open,
                buttonTheme,
                buttonThemeType,
                iconColor
              })}
              shade={(open && activeClassNames?.iconsShade) || iconsShade}
              stroke={iconStroke}
              disabled={disabled}
              className={cn(
                iconClassName,
                icon !== 'context-menu-vertical' && text && 'mr-[6px]',
                disabled && '!cursor-not-allowed'
              )}
            />
          )
        )}

        {/* Text */}
        {typeof text === 'string' ? (
          <span
            className={getTextClasses({
              open,
              buttonTheme,
              textClassName,
              activeClassNames
            })}
            title={text}>
            {text}
          </span>
        ) : (
          text && text
        )}

        {/* Right Icon */}
        {iconRight && !isDropdown && (
          <Icon
            icon={iconRight}
            size={iconRightSize}
            color={getIconRightColor({
              disabled,
              open,
              buttonTheme,
              buttonThemeType,
              iconRightColor,
              activeClassNames
            })}
            shade={iconsShade}
            className={iconRightClassName}
          />
        )}

        {/* Separator */}
        {isSeparator && <hr className={getSeparatorClasses({ buttonTheme, buttonThemeType })} />}

        {/* Dropdown Icon */}
        {isDropdown && (
          <Icon
            icon="chevron-down-linear"
            size={iconRightSize}
            color={getDropdownIconColor({
              open,
              buttonTheme,
              buttonThemeType,
              disabled,
              iconRightColor,
              activeClassNames
            })}
            shade={iconsShade}
            className={cn('ml-1 duration-150', open && 'rotate-180')}
          />
        )}
      </HeadlessPopoverButton>
    </Tippy>
  );
};

export default PopoverButton;
